(() => {
  'use strict';

  const PUBLIC_CODE_RE = /^KS-\d{4}$/i;
  const SKU_RE = /^KS-[A-Z0-9]+(?:-[A-Z0-9]+)+$/i;
  let resolving = false;
  let autoResolveTimer = null;
  let lastAutoCode = '';

  const normalize = (value) => String(value || '').trim().toUpperCase();

  const preservePreviewParams = (url) => {
    const current = new URLSearchParams(window.location.search);
    ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
      const value = current.get(key);
      if (value) url.searchParams.set(key, value);
    });
    return url;
  };

  const productJsUrl = (href) => {
    const url = new URL(href, window.location.origin);
    const match = url.pathname.match(/\/products\/[^/?#]+/i);
    return match ? `${match[0]}.js` : null;
  };

  const tagsForProduct = (product) => {
    if (Array.isArray(product?.tags)) return product.tags.map(normalize);
    return String(product?.tags || '')
      .split(',')
      .map(normalize)
      .filter(Boolean);
  };

  async function resolvePublicCode(rawCode) {
    const code = normalize(rawCode);
    if (!PUBLIC_CODE_RE.test(code) || resolving) return false;
    resolving = true;

    try {
      const catalogUrl = preservePreviewParams(new URL('/products.json', window.location.origin));
      catalogUrl.searchParams.set('limit', '250');
      const response = await fetch(catalogUrl.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Product catalog failed: ${response.status}`);

      const data = await response.json();
      const product = (data.products || []).find((item) => tagsForProduct(item).includes(code));
      if (product?.handle) {
        const destination = preservePreviewParams(
          new URL(`/products/${encodeURIComponent(product.handle)}`, window.location.origin)
        );
        window.location.assign(destination.toString());
        return true;
      }

      const fallback = preservePreviewParams(new URL('/search', window.location.origin));
      fallback.searchParams.set('type', 'product');
      fallback.searchParams.set('q', code);
      window.location.assign(fallback.toString());
      return true;
    } catch (_) {
      const fallback = preservePreviewParams(new URL('/search', window.location.origin));
      fallback.searchParams.set('type', 'product');
      fallback.searchParams.set('q', code);
      window.location.assign(fallback.toString());
      return true;
    } finally {
      setTimeout(() => { resolving = false; }, 500);
    }
  }

  async function resolveSku(rawSku) {
    const sku = normalize(rawSku);
    if (!SKU_RE.test(sku) || resolving) return false;
    resolving = true;

    try {
      const searchUrl = preservePreviewParams(new URL('/search', window.location.origin));
      searchUrl.searchParams.set('type', 'product');
      searchUrl.searchParams.set('q', `variants.sku:${sku}`);

      const response = await fetch(searchUrl.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`Search failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const productLinks = [...doc.querySelectorAll('a[href*="/products/"]')]
        .map((a) => a.getAttribute('href'))
        .filter(Boolean);
      const uniqueProductJs = [...new Set(productLinks.map(productJsUrl).filter(Boolean))].slice(0, 30);

      for (const jsUrl of uniqueProductJs) {
        try {
          const productResponse = await fetch(jsUrl, { credentials: 'same-origin' });
          if (!productResponse.ok) continue;
          const product = await productResponse.json();
          const variant = (product.variants || []).find((item) => normalize(item.sku) === sku);
          if (!variant) continue;

          const destination = preservePreviewParams(new URL(product.url || jsUrl.replace(/\.js$/, ''), window.location.origin));
          destination.searchParams.set('variant', String(variant.id));
          window.location.assign(destination.toString());
          return true;
        } catch (_) {}
      }

      window.location.assign(searchUrl.toString());
      return true;
    } catch (_) {
      const fallback = preservePreviewParams(new URL('/search', window.location.origin));
      fallback.searchParams.set('type', 'product');
      fallback.searchParams.set('q', `variants.sku:${sku}`);
      window.location.assign(fallback.toString());
      return true;
    } finally {
      setTimeout(() => { resolving = false; }, 500);
    }
  }

  function codeFromSearchForm(form) {
    if (!(form instanceof HTMLFormElement)) return '';
    const action = new URL(form.action || '/search', window.location.origin);
    if (!/\/search\/?$/i.test(action.pathname)) return '';
    const input = form.querySelector('input[name="q"]');
    const code = normalize(input?.value);
    return (PUBLIC_CODE_RE.test(code) || SKU_RE.test(code)) ? code : '';
  }

  function resolveCode(code) {
    if (PUBLIC_CODE_RE.test(code)) return resolvePublicCode(code);
    return resolveSku(code);
  }

  document.addEventListener('input', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;

    const code = normalize(input.value);
    clearTimeout(autoResolveTimer);

    if (!PUBLIC_CODE_RE.test(code)) {
      lastAutoCode = '';
      return;
    }

    if (code === lastAutoCode || resolving) return;

    autoResolveTimer = setTimeout(() => {
      const latest = normalize(input.value);
      if (!PUBLIC_CODE_RE.test(latest) || latest === lastAutoCode || resolving) return;
      lastAutoCode = latest;
      resolvePublicCode(latest);
    }, 400);
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target;
    const code = codeFromSearchForm(form);
    if (!code) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    resolveCode(code);
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.name !== 'q') return;
    const code = codeFromSearchForm(input.form);
    if (!code) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    resolveCode(code);
  }, true);

  const params = new URLSearchParams(window.location.search);
  if (/\/search\/?$/i.test(window.location.pathname)) {
    const q = normalize(params.get('q'));
    if (PUBLIC_CODE_RE.test(q) || SKU_RE.test(q)) resolveCode(q);
  }
})();